
import Vue from 'vue';
import { ROUTER } from '@/router/constants';
import { TeamUserMixin } from '@/mixins';
import { mapGetters, mapState, mapActions } from 'vuex';
import { functions } from '@/firebase';
import { SNACKBAR_STATUS } from '@/constants/constants';
import { scimProvisioningMembershipTooltip } from '@/constants/scimProvisioning';
import LoadingSVG from '@/assets/LoadingSVG.vue';
import TeamPlanSignup from '@/views/Team/TeamPlanSignup.vue';
import EditTeams from '@/views/Team/TeamDashboard/EditTeams.vue';
import EditTeamMembers from '@/views/Team/TeamMemberDashboard/EditTeamMembers.vue';
import EditTeamMemberTeams from '@/views/Team/TeamMemberDashboard/EditTeamMemberTeams.vue';
import DoubleConfirmDangerDialog from '@/components/base/DoubleConfirmDangerDialog.vue';
import ConfirmDialog from '@/components/base/ConfirmDialog.vue';
import ListItem from '@/components/base/ListItem.vue';
import { Team, TeamMember, asyncForEach } from '@run-diffusion/shared';
import TransferTokens from '@/views/Team/TeamDashboard/TransferTokens/TransferTokens.vue';
import pluralize from 'pluralize';
import AddTeamToCompany from '@/views/Team/CompanyDashboard/AddTeamToCompany.vue';
import LoadingState from '@/components/states/LoadingState/LoadingState.vue';
import { RUNNITS_OWNER_SELECTION } from '@/views/Runnits/constants';
import Page404 from '@/views/Page404.vue';
import AddCompanyUserRoleDialog from '@/views/Team/CompanyUserRoleDialogs/AddCompanyUserRoleDialog.vue';
import EditCompanyUserRoleDialog from '@/views/Team/CompanyUserRoleDialogs/EditCompanyUserRoleDialog.vue';
import RemoveCompanyUserRoleDialog from '@/views/Team/CompanyUserRoleDialogs/RemoveCompanyUserRoleDialog.vue';
import CompanyNav from '@/views/Team/CompanyNav/CompanyNav.vue';
import BulkCompanyUserRoleDialog from '@/views/Team/CompanyUserRoleDialogs/BulkCompanyUserRoleDialog.vue';
import {
	BULK_COMPANY_ROLE_MODE,
	BulkCompanyRoleMode,
	BulkCompanyRoleTarget,
	CompanyUserRoleTarget,
	EMPTY_COMPANY_USER_ROLE_TARGET,
	companyUserRoleTargetFrom,
} from '@/views/Team/CompanyUserRoleDialogs/companyUserRoleDialogState';
import { removeFromAllTeamsNames } from '@/views/Team/MembersDirectory/membersDirectoryState';
import { noteTeamsAdded, noteTeamsChanged } from '@/utils/teamsMirrorLag';

export default Vue.extend({
	name: 'TeamPage',
	metaInfo () {
		return {
			titleTemplate: '%s | RunDiffusion',
		};
	},
	mixins: [
		TeamUserMixin,
	],
	data () {
		return {
			isStillLoadingInitialPage: false,
			newTeamConfig: {
				open: false,
				duplicateTeam: null,
				onSuccess: () => {},
			},
			editTeamsConfig: {
				open: false,
				teams: [],
				pagination: null,
				onSuccess: () => {},
			},
			transferTokensConfig: {
				open: false,
				team: null,
				onSuccess: () => {},
			},
			editTeamMembersConfig: {
				open: false,
				teamMembers: [],
				team: null,
				onSuccess: () => {},
			},
			deactivateTeamsConfig: {
				open: false,
				teams: [],
				onSuccess: () => {},
			},
			deactivatingTeams: false,
			activateTeamsConfig: {
				open: false,
				teams: [],
				onSuccess: () => {},
			},
			activatingTeams: false,
			removeTeamMembersConfig: {
				open: false,
				teamMembers: [],
				team: null,
				onSuccess: () => {},
			},
			removingTeamMembers: false,
			editTeamMemberTeamsConfig: {
				open: false,
				teams: [],
				teamMember: null,
				onSuccess: () => {},
			},
			removeMemberFromTeamsConfig: {
				open: false,
				teams: [],
				teamMember: null,
				// The Directory's "Remove from all Teams": typed to confirm, and the teams named.
				fromAllTeams: false,
				onSuccess: () => {},
			},
			removingMemberFromTeams: false,
			addCompanyUserRoleConfig: {
				open: false,
				target: { ...EMPTY_COMPANY_USER_ROLE_TARGET } as CompanyUserRoleTarget,
				onSuccess: () => {},
			},
			editCompanyUserRoleConfig: {
				open: false,
				target: { ...EMPTY_COMPANY_USER_ROLE_TARGET } as CompanyUserRoleTarget,
				onSuccess: () => {},
			},
			removeCompanyUserRoleConfig: {
				open: false,
				target: { ...EMPTY_COMPANY_USER_ROLE_TARGET } as CompanyUserRoleTarget,
				onSuccess: () => {},
			},
			bulkCompanyUserRoleConfig: {
				open: false,
				mode: BULK_COMPANY_ROLE_MODE.ASSIGN as BulkCompanyRoleMode,
				targets: [] as BulkCompanyRoleTarget[],
				onSuccess: () => {},
			},
		};
	},
	created () {
		if (this.team && this.team.id) {
			this.isStillLoadingInitialPage = false;
		}

		// Make sure to set it to false after providing enough time to check to see if there is a team or not
		setTimeout(() => {
			this.isStillLoadingInitialPage = false;
		}, 1000);

		// Watch for create new team trigger from Vuex store
	},
	computed: {
		...mapState([
			'loadingTeam',
			'loadingCompanyUserRole',
			'loadingTeamUserRole',
			'isAdminProxyingUser',
			'runnitState',
			'user',
			'team',
			'toolbar',
			'loginQueryParamsData',
			'triggers',
		]),
		...mapGetters([
			'displayLabels',
		]),
		showUserOwnerSelectionNotAllowedUi () {
			return this.runnitState.runnitsOwnerSelection === RUNNITS_OWNER_SELECTION.USER;
		},
		selectedTeamMembers (): TeamMember[] {
			const arr: TeamMember[] = this.removeTeamMembersConfig?.teamMembers || [];
			return Array.isArray(arr) ? arr : [];
		},
		hasAnyAcceptedSelected (): boolean {
			return this.selectedTeamMembers.some((m: TeamMember) => !!m?.acceptedAt);
		},
		hasAnyPendingSelected (): boolean {
			return this.selectedTeamMembers.some((m: TeamMember) => !m?.acceptedAt);
		},
		removeMemberFromTeamsDialogTitle (): string {
			const teams: Team[] = this.removeMemberFromTeamsConfig?.teams || [];
			const count: number = teams?.length || 0;
			const teamsLabel: string = pluralize(this.displayLabels.team, count);
			const email: string = this.removeMemberFromTeamsConfig?.teamMember?.email || '';
			return `Remove ${email} from ${count} ${teamsLabel}`;
		},
		removeMemberFromTeamsNames (): string {
			return removeFromAllTeamsNames(this.removeMemberFromTeamsConfig?.teams || []);
		},
		// What removing them leaves alone. Their company role is mentioned only to those who see company roles.
		removeMemberFromTeamsKeeps (): string {
			return this.hasManageCompanyPermission
				? 'This will not change their company role or delete their personal account.'
				: 'This will not delete their personal account.';
		},
	},
	watch: {
		team (newVal: Team, oldVal: Team) {
			const oldCompanyId: string = oldVal?.companyId || null;
			const newCompanyId: string = newVal?.companyId || null;
			if (newCompanyId && oldCompanyId && oldCompanyId !== newCompanyId && this.$route.name !== ROUTER.COMPANY_DASHBOARD) {
				this.routerReplace(this.$route, this.$router, {
					name: ROUTER.COMPANY_DASHBOARD,
					query: this.$route.query,
				});
			}
			if (newVal !== oldVal && newVal) {
				this.isStillLoadingInitialPage = false;
			}
		},
		'$route.name': {
			immediate: true,
			handler (newVal: string, oldVal: string) {
				if (newVal !== oldVal) {
					// scroll to top with smooth animation
					window.scrollTo({
						top: 0,
						left: 0,
						behavior: 'smooth'
					});
				}
			},
		},
		'triggers.createNewTeamTrigger': {
			handler (newVal: number, oldVal: number) {
				// Only trigger if the value actually changed and we're on the company dashboard route
				if (newVal !== oldVal && newVal > 0 && this.$route.name === ROUTER.COMPANY_DASHBOARD) {
					this.handleCreateNewTeamEvent();
				}
			},
		},
	},
	methods: {
		...mapActions([
			'updateSnackbar',
			'unbindAllTeamData',
		]),
		pluralize,
		onTeamPlanSignupBack () {
			this.goHome();
		},
		goHome () {
			if (this.team && this.team.isOnboarding) {
				// unbind so that if they had belonged to more than one team, that the team page will re-query and make them reselect it when revisited
				this.unbindAllTeamData();
			}

			const hasCurrentSessionId: boolean = !!(this._get(this.toolbar, 'session.id'));

			if (hasCurrentSessionId) {
				this.routerPush(this.$route, this.$router, { name: ROUTER.LAUNCH, params: { sessionId: this._get(this.toolbar, 'session.id') } });
			} else {
				this.routerPush(this.$route, this.$router, { name: ROUTER.SESSIONS });
			}
		},
		/**
		 * A dialog that changes Teams or memberships also says so on the teams channels
		 * (teamsMirrorLag.ts), whoever opened it. The dashboard cards read their counts from the
		 * mirror and catch up once it has the write, including on a page opened next. An edit
		 * (a rename, a role, deactivating or reactivating) leaves the tables as they are: their
		 * rows follow it live and stay where they are until Refresh. Someone removed is seen by
		 * their own row's binding, which marks the row and asks its table again. Not Transfer
		 * Tokens or the company role dialogs: neither changes what the mirror lists.
		 */
		noteTeamsChangedThen (onSuccess?: () => void): () => void {
			return () => {
				noteTeamsChanged();
				if (onSuccess) onSuccess();
			};
		},
		// A new Team: the tables catch up too, so it shows up in them
		noteTeamsAddedThen (onSuccess?: () => void): () => void {
			return () => {
				noteTeamsAdded();
				if (onSuccess) onSuccess();
			};
		},
		onNewTeam ({ duplicateTeam, onSuccess }) {
			this.newTeamConfig = {
				open: true,
				duplicateTeam: duplicateTeam || null,
				onSuccess: this.noteTeamsAddedThen(onSuccess),
			};
		},
		handleCreateNewTeamEvent() {
			// Trigger the new team creation flow
			this.onNewTeam({});
		},
		onEditTeams ({ teams, pagination, onSuccess }) {
			this.editTeamsConfig = {
				open: true,
				teams,
				pagination: pagination || null,
				onSuccess: this.noteTeamsChangedThen(onSuccess),
			};
		},
		onTransferTokens ({ team, onSuccess }) {
			this.transferTokensConfig = {
				open: true,
				team,
				onSuccess: onSuccess || (() => {}),
			};
		},
		onEditTeamMembers ({ teamMembers, team, onSuccess }) {
			if (this.scimProvisioningLocksMembership) {
				this.updateSnackbar({ status: SNACKBAR_STATUS.ERROR, message: scimProvisioningMembershipTooltip(this.displayLabels), show: true });
				return;
			}
			this.editTeamMembersConfig = {
				open: true,
				teamMembers,
				team,
				onSuccess: this.noteTeamsChangedThen(onSuccess),
			};
		},
		onDeactivateTeams ({ teams, onSuccess }) {
			if (!(teams || []).length) return;
			this.deactivateTeamsConfig = {
				open: true,
				teams: teams || [],
				onSuccess: this.noteTeamsChangedThen(onSuccess),
			};
		},
		onActivateTeams ({ teams, onSuccess }) {
			this.activateTeamsConfig = {
				open: true,
				teams,
				onSuccess: this.noteTeamsChangedThen(onSuccess),
			};
		},
		onRemoveTeamMembers ({ teamMembers, team, onSuccess }) {
			if (this.scimProvisioningLocksMembership) {
				this.updateSnackbar({ status: SNACKBAR_STATUS.ERROR, message: scimProvisioningMembershipTooltip(this.displayLabels), show: true });
				return;
			}
			this.removeTeamMembersConfig = {
				open: true,
				teamMembers,
				team,
				onSuccess: this.noteTeamsChangedThen(onSuccess),
			};
		},
		onEditTeamMemberTeams ({ teams, teamMember, onSuccess }) {
			if (this.scimProvisioningLocksMembership) {
				this.updateSnackbar({ status: SNACKBAR_STATUS.ERROR, message: scimProvisioningMembershipTooltip(this.displayLabels), show: true });
				return;
			}
			this.editTeamMemberTeamsConfig = {
				open: true,
				teams,
				teamMember,
				onSuccess: this.noteTeamsChangedThen(onSuccess),
			};
		},
		onRemoveMemberFromTeams ({ teams, teamMember, fromAllTeams, onSuccess }) {
			if (this.scimProvisioningLocksMembership) {
				this.updateSnackbar({ status: SNACKBAR_STATUS.ERROR, message: scimProvisioningMembershipTooltip(this.displayLabels), show: true });
				return;
			}
			this.removeMemberFromTeamsConfig = {
				open: true,
				teams: Array.isArray(teams) ? teams : [],
				teamMember,
				fromAllTeams: !!fromAllTeams,
				onSuccess: this.noteTeamsChangedThen(onSuccess),
			};
		},
		// The Directory's bulk Assign and Remove Company Role. The dialog leaves out anyone it would
		// not change, and the viewer's own row, as the one-person handlers refuse it.
		onBulkCompanyUserRole ({ mode, targets, onSuccess }) {
			if (this.scimProvisioningLocksMembership) {
				this.updateSnackbar({ status: SNACKBAR_STATUS.ERROR, message: scimProvisioningMembershipTooltip(this.displayLabels), show: true });
				return;
			}
			this.bulkCompanyUserRoleConfig = {
				open: true,
				mode: mode === BULK_COMPANY_ROLE_MODE.REMOVE ? BULK_COMPANY_ROLE_MODE.REMOVE : BULK_COMPANY_ROLE_MODE.ASSIGN,
				targets: Array.isArray(targets) ? targets : [],
				onSuccess: onSuccess || (() => {}),
			};
		},
		// row is null for the directory's page-level Add: an address typed in by hand.
		onAddCompanyUserRole ({ row, email, userId, companyUserRoleDefId, onSuccess }) {
			if (this.scimProvisioningLocksMembership) {
				this.updateSnackbar({ status: SNACKBAR_STATUS.ERROR, message: scimProvisioningMembershipTooltip(this.displayLabels), show: true });
				return;
			}
			this.addCompanyUserRoleConfig = {
				open: true,
				target: companyUserRoleTargetFrom({ row, email, userId, companyUserRoleDefId }),
				onSuccess: onSuccess || (() => {}),
			};
		},
		onEditCompanyUserRole ({ row, email, userId, companyUserRoleDefId, onSuccess }) {
			if (this.scimProvisioningLocksMembership) {
				this.updateSnackbar({ status: SNACKBAR_STATUS.ERROR, message: scimProvisioningMembershipTooltip(this.displayLabels), show: true });
				return;
			}
			// Add safety check to prevent editing own role
			if (!this.user.isAdmin && userId && userId === this.user.id) {
				this.updateSnackbar({ status: SNACKBAR_STATUS.ERROR, message: 'You cannot edit your own role', show: true });
				return;
			}
			this.editCompanyUserRoleConfig = {
				open: true,
				target: companyUserRoleTargetFrom({ row, email, userId, companyUserRoleDefId }),
				onSuccess: onSuccess || (() => {}),
			};
		},
		onRemoveCompanyUserRole ({ row, email, userId, companyUserRoleDefId, onSuccess }) {
			if (this.scimProvisioningLocksMembership) {
				this.updateSnackbar({ status: SNACKBAR_STATUS.ERROR, message: scimProvisioningMembershipTooltip(this.displayLabels), show: true });
				return;
			}
			// Add safety check to prevent removing own role
			if (!this.user.isAdmin && userId && userId === this.user.id) {
				this.updateSnackbar({ status: SNACKBAR_STATUS.ERROR, message: 'You cannot remove your own role', show: true });
				return;
			}
			this.removeCompanyUserRoleConfig = {
				open: true,
				target: companyUserRoleTargetFrom({ row, email, userId, companyUserRoleDefId }),
				onSuccess: onSuccess || (() => {}),
			};
		},
		onCloseDeactivateTeamsDialog () {
			this.deactivateTeamsConfig = {
				...this.deactivateTeamsConfig,
				open: false,
			};
		},
		onCloseActivateTeamsDialog () {
			this.activateTeamsConfig = {
				...this.activateTeamsConfig,
				open: false,
			};
		},
		onCloseRemoveTeamMemberDialog () {
			this.removeTeamMembersConfig = {
				...this.removeTeamMembersConfig,
				open: false,
			};
		},
		onCloseRemoveMemberFromTeamsDialog () {
			this.removeMemberFromTeamsConfig = {
				...this.removeMemberFromTeamsConfig,
				open: false,
			};
		},
		async handleDeactivateTeamsConfirm (closeAndResetCallback: Function) {
			if (this.deactivatingTeams || !this.deactivateTeamsConfig.teams.length) return;

			const onError: Function = (e) => {
				console.error('Error deactivating teams:', e);
				this.updateSnackbar({
					status: SNACKBAR_STATUS.ERROR,
					message: `Error deactivating ${pluralize(this.displayLabels.team, this.deactivateTeamsConfig.teams.length)}, please try again`,
					show: true,
				});
			};

			try {
				this.deactivatingTeams = true;
				const functionRef = functions.httpsCallable('toggleDeactivateCompanyTeams');
				const { success, message } = (await functionRef({
					teamIds: this.deactivateTeamsConfig.teams.map(({ id }) => id),
					deactivate: true,
				})).data;

				if (!success) {
					onError(new Error(message || 'toggleDeactivateCompanyTeams returned unsuccessful response'));
				} else {
					this.updateSnackbar({
						status: SNACKBAR_STATUS.SUCCESS,
						message: `Success! ${pluralize(this.displayLabels.team, this.deactivateTeamsConfig.teams.length)} has been deactivated`,
						show: true,
					});
					this.deactivateTeamsConfig?.onSuccess();
				}
			} catch (e) {
				onError(e);
			} finally {
				this.deactivatingTeams = false;
				this.onCloseDeactivateTeamsDialog();
				if (closeAndResetCallback) {
					closeAndResetCallback();
				}
			}
		},
		async handleActivateTeamsConfirm (closeAndResetCallback: Function) {
			if (this.activatingTeams || !this.activateTeamsConfig.teams.length) return;

			const onError: Function = (e) => {
				console.error('Error activating teams:', e);
				this.updateSnackbar({
					status: SNACKBAR_STATUS.ERROR,
					message: `Error activating ${pluralize(this.displayLabels.team, this.activateTeamsConfig.teams.length)}, please try again`,
					show: true,
				});
			};

			try {
				this.activatingTeams = true;
				const functionRef = functions.httpsCallable('toggleDeactivateCompanyTeams');
				const { success, message } = (await functionRef({
					teamIds: this.activateTeamsConfig.teams.map(({ id }) => id),
					deactivate: false,
				})).data;

				if (!success) {
					onError(new Error(message || 'toggleDeactivateCompanyTeams returned unsuccessful response'));
				} else {
					this.updateSnackbar({
						status: SNACKBAR_STATUS.SUCCESS,
						message: `Success! ${pluralize(this.displayLabels.team, this.activateTeamsConfig.teams.length)} has been activated`,
						show: true,
					});
					this.activateTeamsConfig?.onSuccess();
				}
			} catch (e) {
				onError(e);
			} finally {
				this.activatingTeams = false;
				this.onCloseActivateTeamsDialog();
				if (closeAndResetCallback) {
					closeAndResetCallback();
				}
			}
		},
		async onConfirmRemoveMemberFromTeams (closeAndResetCallback: Function) {
			if (this.scimProvisioningLocksMembership) {
				if (closeAndResetCallback) closeAndResetCallback();
				this.onCloseRemoveMemberFromTeamsDialog();
				return;
			}
			if (this.removingMemberFromTeams) return;
			const teams: Team[] = this.removeMemberFromTeamsConfig?.teams || [];
			const member: TeamMember = this.removeMemberFromTeamsConfig?.teamMember;
			const selfUserId: string = this.user?.id;

			if (!teams.length || !member?.email) {
				if (closeAndResetCallback) closeAndResetCallback();
				return;
			}

			// Prevent removing yourself
			if (member?.userId && selfUserId && member.userId === selfUserId) {
				this.updateSnackbar({
					status: SNACKBAR_STATUS.ERROR,
					message: `Cannot remove yourself from ${pluralize(this.displayLabels.team)}`,
					show: true,
				});
				if (closeAndResetCallback) closeAndResetCallback();
				this.onCloseRemoveMemberFromTeamsDialog();
				return;
			}

			try {
				this.removingMemberFromTeams = true;
				const removeFn = functions.httpsCallable('deleteTeamMember');
				
				// Cannot use Promise.all here because concurrent deleteTeamMember calls 
				// interfere with each other due to write batch preconditions in the backend.
				// Must process each team member deletion sequentially.
				const results: any[] = [];
				await asyncForEach(teams, async (team: Team) => {
					const teamId: string = team?.id;
					const email: string = member?.email;
					if (!teamId || !email) {
						results.push({ teamId, success: false, error: new Error('Missing data') });
						return;
					}
					try {
						const { success, message } = (await removeFn({ teamId, teamMemberEmail: email })).data || {};
						results.push({ teamId, success: !!success, message });
					} catch (error) {
						results.push({ teamId, success: false, error });
					}
				});

				const successCount = results.filter((r: any) => r.success).length;
				const failed = results.filter((r: any) => !r.success);
				// The server says why when it can (the company API token's own user cannot be removed); a bare count would not.
				const failedReason: string = (failed.find((r: any) => r.message) || {}).message || '';

				if (!failed.length) {
					this.updateSnackbar({
						status: SNACKBAR_STATUS.SUCCESS,
						message: `Removed from ${successCount} ${pluralize(this.displayLabels.team, successCount)}`,
						show: true,
					});
					this.removeMemberFromTeamsConfig?.onSuccess();
				} else {
					this.updateSnackbar({
						status: SNACKBAR_STATUS.ERROR,
						message: `Removed ${successCount}, failed ${failed.length}${failedReason ? `: ${failedReason}` : ''}`,
						show: true,
					});
				}
			} catch (e) {
				console.error('Error removing member from teams:', e);
				this.updateSnackbar({
					status: SNACKBAR_STATUS.ERROR,
					message: `Error removing from ${pluralize(this.displayLabels.team)}, please try again`,
					show: true,
				});
			} finally {
				this.removingMemberFromTeams = false;
				this.onCloseRemoveMemberFromTeamsDialog();
				if (closeAndResetCallback) closeAndResetCallback();
			}
		},

		async onConfirmRemoveTeamMembers (closeAndResetCallback: Function) {
			if (this.scimProvisioningLocksMembership) {
				if (closeAndResetCallback) closeAndResetCallback();
				this.onCloseRemoveTeamMemberDialog();
				return;
			}
			if (this.removingTeamMembers || !this.removeTeamMembersConfig.teamMembers || !this.removeTeamMembersConfig.team) return;

			const teamId: string = this.removeTeamMembersConfig.team.id;
			const members: TeamMember[] = this.removeTeamMembersConfig.teamMembers || [];
			const selfUserId: string = this.user?.id;
			const toDelete = members.filter((m: TeamMember) => m?.userId !== selfUserId);
			const skippedSelfCount = members.length - toDelete.length;

			if (!toDelete.length) {
				this.updateSnackbar({
					status: SNACKBAR_STATUS.ERROR,
					message: `Cannot remove yourself from the ${this.displayLabels.team}`,
					show: true,
				});
				return;
			}

			try {
				this.removingTeamMembers = true;
				const removeFn = functions.httpsCallable('deleteTeamMember');
				
				// Cannot use Promise.all here because concurrent deleteTeamMember calls 
				// interfere with each other due to write batch preconditions in the backend.
				// Must process each team member deletion sequentially.
				const results: any[] = [];
				await asyncForEach(toDelete, async (member: TeamMember) => {
					const email: string = member?.email;
					if (!email) {
						results.push({ email, success: false, error: new Error('Missing email for member') });
						return;
					}
					try {
						const { success } = (await removeFn({ teamId, teamMemberEmail: email })).data || {};
						results.push({ email, success: !!success });
					} catch (error) {
						results.push({ email, success: false, error });
					}
				});

				const successCount = results.filter((r: any) => r.success).length;
				const failed = results.filter((r: any) => !r.success);

				if (!failed.length) {
					this.updateSnackbar({
						status: SNACKBAR_STATUS.SUCCESS,
						message: `Removed ${successCount} ${pluralize('member', successCount)}${skippedSelfCount ? ` (skipped ${skippedSelfCount} self)` : ''}`,
						show: true,
					});
					this.removeTeamMembersConfig?.onSuccess();
				} else {
					console.error('Error deleting team members:', failed);
					this.updateSnackbar({
						status: SNACKBAR_STATUS.ERROR,
						message: `Removed ${successCount}, failed ${failed.length}${skippedSelfCount ? `, skipped ${skippedSelfCount} self` : ''}`,
						show: true,
					});
				}
			} catch (e) {
				console.error('Error deleting team members:', e);
				this.updateSnackbar({
					status: SNACKBAR_STATUS.ERROR,
					message: 'Error deleting members, please try again',
					show: true,
				});
			} finally {
				this.removingTeamMembers = false;
				this.onCloseRemoveTeamMemberDialog();
				if (closeAndResetCallback) {
					closeAndResetCallback();
				}
			}
		},
	},
	components: {
		Page404,
		LoadingState,
		AddTeamToCompany,
		EditTeams,
		EditTeamMembers,
		EditTeamMemberTeams,
		TeamPlanSignup,
		LoadingSVG,
		DoubleConfirmDangerDialog,
		ListItem,
		TransferTokens,
		ConfirmDialog,
		AddCompanyUserRoleDialog,
		EditCompanyUserRoleDialog,
		RemoveCompanyUserRoleDialog,
		BulkCompanyUserRoleDialog,
		CompanyNav,
	},
});
